// ============================================
// PlaceholderModal — Fill {{variables}} one at a
// time with skip support. Progress persists via
// doc content (filled vars are replaced inline).
// ============================================

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { C } from './docopsConstants';

const GOLD = '#C9A96E';

function camelToReadable(s: string): string {
  return s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}

function getContextLine(content: string, varName: string): string {
  const pattern = `{{${varName}}}`;
  const idx = content.indexOf(pattern);
  if (idx === -1) return '';
  const lineStart = content.lastIndexOf('\n', idx) + 1;
  let lineEnd = content.indexOf('\n', idx + pattern.length);
  if (lineEnd === -1) lineEnd = content.length;
  let line = content.slice(lineStart, lineEnd).trim();
  if (line.length > 140) {
    const varPos = idx - lineStart;
    const start = Math.max(0, varPos - 50);
    const end = Math.min(line.length, varPos + pattern.length + 50);
    line = (start > 0 ? '...' : '') + line.slice(start, end) + (end < line.length ? '...' : '');
  }
  return line;
}

interface PlaceholderModalProps {
  content: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveValue: (varName: string, value: string) => Promise<void>;
}

export const PlaceholderModal: React.FC<PlaceholderModalProps> = ({
  content,
  isOpen,
  onClose,
  onSaveValue,
}) => {
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [filledCount, setFilledCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Extract remaining unique {{variables}} from content
  const allVars = useMemo(() => {
    const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.slice(2, -2)))];
  }, [content]);

  const remaining = allVars.filter(v => !skipped.has(v));
  const current = remaining[0] || null;
  const totalEstimate = filledCount + allVars.length;
  const progress = totalEstimate > 0 ? Math.round((filledCount / totalEstimate) * 100) : 100;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSkipped(new Set());
      setValue('');
      setSaving(false);
      setFilledCount(0);
    }
  }, [isOpen]);

  // Focus input when current variable changes
  useEffect(() => {
    if (isOpen && current && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, current]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!value.trim() || !current || saving) return;
    setSaving(true);
    try {
      await onSaveValue(current, value.trim());
      setValue('');
      setFilledCount(c => c + 1);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (!current) return;
    setSkipped(prev => new Set([...prev, current]));
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  // All variables filled
  if (allVars.length === 0) {
    return (
      <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>&#10003;</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.success, marginBottom: 8 }}>
              All Variables Filled
            </div>
            <div style={{ fontSize: 13, color: C.textSec, marginBottom: 24 }}>
              {filledCount > 0
                ? `${filledCount} value${filledCount !== 1 ? 's' : ''} saved this session.`
                : 'No remaining placeholders in this document.'}
            </div>
            <button onClick={onClose} style={primaryBtnStyle}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // All remaining skipped
  if (!current) {
    return (
      <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>
              {skipped.size} Variable{skipped.size !== 1 ? 's' : ''} Skipped
            </div>
            {filledCount > 0 && (
              <div style={{ fontSize: 13, color: C.success, marginBottom: 8 }}>
                {filledCount} value{filledCount !== 1 ? 's' : ''} saved.
              </div>
            )}
            <div style={{ fontSize: 13, color: C.textSec, marginBottom: 24 }}>
              Come back when you have the remaining values.
            </div>
            <button onClick={onClose} style={primaryBtnStyle}>Done for Now</button>
          </div>
        </div>
      </div>
    );
  }

  const context = getContextLine(content, current);

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${C.borderLight}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              Fill Document Variables
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
              {filledCount} filled &middot; {remaining.length} remaining
              {skipped.size > 0 ? ` \u00B7 ${skipped.size} skipped` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20, color: C.textMuted,
            cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
          }}>{'\u2715'}</button>
        </div>

        {/* Progress bar */}
        <div style={{ padding: '8px 20px 0' }}>
          <div style={{
            height: 4, background: C.borderLight, borderRadius: 2, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: GOLD, borderRadius: 2, transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          <div style={{
            fontSize: 16, fontWeight: 700, color: GOLD, marginBottom: 12,
          }}>
            {camelToReadable(current)}
          </div>

          {context && (
            <div style={{
              fontSize: 12, color: C.textSec, background: C.bgSoft,
              padding: '10px 12px', borderRadius: 3, marginBottom: 16,
              fontFamily: 'Georgia, serif', lineHeight: 1.6,
              borderLeft: `3px solid ${GOLD}`, whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {context.replace(
                `{{${current}}}`,
                `\u27E8${camelToReadable(current)}\u27E9`,
              )}
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Enter value for ${camelToReadable(current)}...`}
            disabled={saving}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 3,
              border: `1px solid ${value.trim() ? GOLD : C.border}`,
              fontSize: 14, fontFamily: 'inherit', color: C.text,
              background: '#FFFFFF', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          borderTop: `1px solid ${C.borderLight}`,
        }}>
          <button onClick={onClose} style={secondaryBtnStyle}>
            Done for Now
          </button>
          <button onClick={handleSkip} style={secondaryBtnStyle}>
            Skip
          </button>
          <button
            onClick={handleSave}
            disabled={!value.trim() || saving}
            style={{
              padding: '8px 20px', borderRadius: 3,
              background: value.trim() && !saving ? GOLD : C.borderLight,
              color: value.trim() && !saving ? '#FFFFFF' : C.textMuted,
              border: 'none', fontSize: 13, fontWeight: 600,
              cursor: value.trim() && !saving ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Saving...' : 'Save & Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Styles ──

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: '#FFFFFF',
  borderRadius: 6,
  width: '100%',
  maxWidth: 500,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '10px 28px', borderRadius: 3,
  background: GOLD, color: '#FFFFFF', border: 'none',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 3,
  background: 'none', border: `1px solid #E4E4E7`,
  fontSize: 13, color: '#71717A', cursor: 'pointer',
  fontFamily: 'inherit',
};
